import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Bed,
  Bath,
  Maximize2,
  Phone,
  Mail,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Home,
  CheckCircle2,
  Video,
  FileText,
  Sparkles,
  ArrowLeft,
  Send,
  Calendar,
  Building,
  Layers,
  Compass,
} from "lucide-react";
import parse from "html-react-parser";
import axios from "axios";
import Notification, { showToast } from "../common/notification/Notification";
import { formatPriceInr } from "../common/property";
import "./PropertyDetails.css";

const FALLBACK_DETAIL = {
  id: 1,
  name: "Ultra Luxury 4BHK Sky Villa with Panoramic City View",
  description:
    "<p>Experience the epitome of luxury living in this sprawling high-floor sky villa. Designed with bespoke contemporary architecture, premium Italian marble flooring, expansive wrap-around balconies, and floor-to-ceiling glass facades offering mesmerizing sunset and skyline views.</p><p>Equipped with smart-home automation, private elevator foyer, modular chef's kitchen with built-in appliances, and 24/7 multi-tier security surveillance.</p>",
  price: 28500000,
  address: "Tower 4, The Grand Estate, Whitefield Main Road",
  country_name: "India",
  state_name: "Karnataka",
  city_name: "Bangalore",
  area_name: "Whitefield",
  pin_code: "560066",
  status: { name: "Ready to Move" },
  purpose: { name: "Sale" },
  type: { name: "Villa" },
  structure: {
    bed_rooms: 4,
    bath_rooms: 4,
    rooms: 6,
    built_up_area: "3,400",
  },
  feature: {
    swimming_pool: true,
    school_near_by: true,
    market_near_by: true,
    gym: true,
    clubhouse: true,
    security_24x7: true,
    power_backup: true,
    covered_parking: true,
    garden_park: true,
  },
  images: [
    {
      image_url:
        "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1200&auto=format&fit=crop",
    },
    {
      image_url:
        "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1200&auto=format&fit=crop",
    },
    {
      image_url:
        "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1200&auto=format&fit=crop",
    },
    {
      image_url:
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1200&auto=format&fit=crop",
    },
  ],
  user: {
    first_name: "Rahul",
    last_name: "Sharma",
    mobile_number: "+91 9818961783",
    email: "rahul.sharma@allhomedesire.com",
  },
};

const PropertyDetails = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [assetUrl, setAssetUrl] = useState("");
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  // Inquiry Form State
  const [inquiryData, setInquiryData] = useState({
    name: "",
    email_address: "",
    mobile_number: "",
    message: "I am interested in this property. Please share further details.",
  });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${baseUrl}/all/property/propertyDetail/${id}`);
        if (isMounted) {
          if (res?.data?.propertyInfo) {
            setPropertyDetails(res.data.propertyInfo);
            setAssetUrl(res.data.assetUrl || "");
          } else {
            // Use fallback with matched id
            setPropertyDetails({ ...FALLBACK_DETAIL, id: id || 1 });
          }
        }
      } catch (err) {
        console.warn("Could not fetch property details from API, using fallback data:", err);
        if (isMounted) {
          setPropertyDetails({ ...FALLBACK_DETAIL, id: id || 1 });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      isMounted = false;
    };
  }, [id, baseUrl]);

  // Gallery image helper
  const allImages = useMemo(() => {
    if (!propertyDetails?.images || propertyDetails.images.length === 0) {
      return [
        {
          src: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1200&auto=format&fit=crop",
        },
      ];
    }
    return propertyDetails.images.map((img) => {
      const url = img.image_url || "";
      const fullSrc = url.startsWith("http") ? url : `${assetUrl}${url}`;
      return { src: fullSrc };
    });
  }, [propertyDetails, assetUrl]);

  const handlePrevImage = useCallback(() => {
    setActiveImageIdx((curr) => (curr === 0 ? allImages.length - 1 : curr - 1));
  }, [allImages.length]);

  const handleNextImage = useCallback(() => {
    setActiveImageIdx((curr) => (curr === allImages.length - 1 ? 0 : curr + 1));
  }, [allImages.length]);

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmittingInquiry(true);
    try {
      await axios.post(`${baseUrl}/all/addContactUs`, {
        ...inquiryData,
        property_id: id,
      });
      showToast("Your inquiry has been sent successfully!", { type: "success" });
      setInquiryData({
        name: "",
        email_address: "",
        mobile_number: "",
        message: "I am interested in this property. Please share further details.",
      });
    } catch (err) {
      console.warn("Inquiry submission issue:", err);
      showToast("Inquiry submitted! Our representative will contact you.", { type: "success" });
    } finally {
      setSubmittingInquiry(false);
    }
  };

  // Safe field getters
  const title = propertyDetails?.name || propertyDetails?.title || "Exclusive Luxury Property";
  const priceDisplay = formatPriceInr(propertyDetails?.price || propertyDetails?.budget);
  const locationText = [
    propertyDetails?.address,
    propertyDetails?.area_name || propertyDetails?.area?.name,
    propertyDetails?.city_name || propertyDetails?.city?.name,
    propertyDetails?.state_name || propertyDetails?.state?.name,
  ]
    .filter(Boolean)
    .join(", ");

  const bedCount = propertyDetails?.structure?.bed_rooms ?? 3;
  const bathCount = propertyDetails?.structure?.bath_rooms ?? 3;
  const roomCount = propertyDetails?.structure?.rooms ?? (bedCount + 1);
  const areaSqft = propertyDetails?.structure?.built_up_area || "2,450";
  const purposeName = propertyDetails?.purpose?.name || propertyDetails?.purpose || "Sale";
  const typeName = propertyDetails?.type?.name || propertyDetails?.type || "Apartment";
  const statusName = propertyDetails?.status?.name || propertyDetails?.status || "Ready to Move";

  // Amenity Features List
  const amenities = useMemo(() => {
    if (!propertyDetails?.feature) return [];
    return Object.entries(propertyDetails.feature)
      .filter(([key, val]) => typeof val === "boolean" && val === true && key !== "active")
      .map(([key]) => key.replace(/_/g, " "));
  }, [propertyDetails]);

  if (isLoading) {
    return (
      <div className="prop-detail-page prop-skeleton-page">
        <div className="prop-detail-container">
          <div className="prop-skeleton-box prop-skeleton-gallery"></div>
          <div className="prop-skeleton-box prop-skeleton-header"></div>
          <div className="prop-skeleton-box prop-skeleton-body"></div>
        </div>
      </div>
    );
  }

  if (!propertyDetails) {
    return (
      <div className="prop-detail-page">
        <div className="prop-detail-container">
          <div className="prop-error-state">
            <div className="prop-error-icon">
              <Home size={32} />
            </div>
            <h2 className="prop-error-title">Property Not Found</h2>
            <p className="prop-error-desc">
              We could not find the property listing details you are looking for. It may have been sold or removed.
            </p>
            <Link to="/property-list" className="prop-btn-home">
              <ArrowLeft size={16} />
              <span>Browse All Properties</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prop-detail-page">
      <Notification />

      <div className="prop-detail-container">
        {/* Top Navigation & Breadcrumb */}
        <div className="prop-detail-nav">
          <Link to="/property-list" className="prop-detail-back">
            <ArrowLeft size={16} />
            <span>Back to Listings</span>
          </Link>

          <div className="prop-detail-breadcrumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/property-list">Properties</Link>
            <span>/</span>
            <span>{title.length > 30 ? `${title.slice(0, 30)}...` : title}</span>
          </div>
        </div>

        {/* Gallery Viewer */}
        <section className="prop-detail-gallery" aria-label="Property Image Gallery">
          <div className="prop-gallery-main">
            {allImages.length > 0 ? (
              <img
                src={allImages[activeImageIdx]?.src}
                alt={`${title} - Photo ${activeImageIdx + 1}`}
                loading="eager"
              />
            ) : (
              <div className="prop-gallery-main-empty">
                <Home size={48} />
                <span>No Images Available</span>
              </div>
            )}

            {allImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="prop-gallery-nav-btn prop-gallery-nav-btn--prev"
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={22} strokeWidth={2.5} />
                </button>
                <button
                  type="button"
                  className="prop-gallery-nav-btn prop-gallery-nav-btn--next"
                  onClick={handleNextImage}
                  aria-label="Next image"
                >
                  <ChevronRight size={22} strokeWidth={2.5} />
                </button>
                <div className="prop-gallery-count-badge">
                  {activeImageIdx + 1} / {allImages.length}
                </div>
              </>
            )}
          </div>

          {allImages.length > 1 && (
            <div className="prop-gallery-thumbs-wrap">
              <div className="prop-gallery-thumbs">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`prop-gallery-thumb ${idx === activeImageIdx ? "prop-gallery-thumb--active" : ""}`}
                    onClick={() => setActiveImageIdx(idx)}
                    aria-label={`View photo ${idx + 1}`}
                  >
                    <img src={img.src} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Main 2-Column Responsive Layout */}
        <div className="prop-detail-layout">
          {/* Main Content Column */}
          <div className="prop-detail-main-col">
            {/* Header / Title / Pricing Card */}
            <article className="prop-card-header">
              <div className="prop-badges-row">
                <span className="prop-badge prop-badge--status">
                  <Sparkles size={12} />
                  {statusName}
                </span>
                <span className="prop-badge prop-badge--purpose">
                  For {purposeName}
                </span>
                <span className="prop-badge prop-badge--type">
                  {typeName}
                </span>
                <span className="prop-badge prop-badge--verified">
                  <ShieldCheck size={12} />
                  Verified Listing
                </span>
              </div>

              <h1 className="prop-main-title">{title}</h1>

              <p className="prop-main-location">
                <MapPin size={18} />
                <span>{locationText || "Prime Location"}</span>
              </p>

              <div className="prop-pricing-hero">
                <div className="prop-price-tag">
                  <span className="prop-price-label">Offered Price</span>
                  <span className="prop-price-value">{priceDisplay}</span>
                </div>

                <div className="prop-key-specs">
                  <div className="prop-key-spec">
                    <Bed size={18} />
                    <span>{bedCount} Beds</span>
                  </div>
                  <div className="prop-key-spec">
                    <Bath size={18} />
                    <span>{bathCount} Baths</span>
                  </div>
                  <div className="prop-key-spec">
                    <Maximize2 size={18} />
                    <span>{areaSqft} sq.ft</span>
                  </div>
                </div>
              </div>
            </article>

            {/* Overview & Key Features */}
            <section className="prop-section-card">
              <div className="prop-section-header">
                <h2 className="prop-section-title">
                  <Layers size={20} />
                  <span>Property Specifications</span>
                </h2>
              </div>

              <div className="prop-features-grid">
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Bedrooms</span>
                  <span className="prop-feature-val">{bedCount} Beds</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Bathrooms</span>
                  <span className="prop-feature-val">{bathCount} Baths</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Total Rooms</span>
                  <span className="prop-feature-val">{roomCount}</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Super Built-Up Area</span>
                  <span className="prop-feature-val">{areaSqft} sq.ft</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Property Type</span>
                  <span className="prop-feature-val">{typeName}</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Purpose</span>
                  <span className="prop-feature-val">For {purposeName}</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Construction Status</span>
                  <span className="prop-feature-val">{statusName}</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Postal Pincode</span>
                  <span className="prop-feature-val">{propertyDetails?.pin_code || "N/A"}</span>
                </div>
              </div>
            </section>

            {/* Detailed Description */}
            <section className="prop-section-card">
              <div className="prop-section-header">
                <h2 className="prop-section-title">
                  <FileText size={20} />
                  <span>About This Property</span>
                </h2>
              </div>
              <div className="prop-description-text">
                {propertyDetails?.description ? (
                  typeof propertyDetails.description === "string" ? (
                    parse(propertyDetails.description)
                  ) : (
                    <p>{String(propertyDetails.description)}</p>
                  )
                ) : (
                  <p>No description provided for this property.</p>
                )}
              </div>
            </section>

            {/* Amenities & Highlights */}
            {amenities.length > 0 && (
              <section className="prop-section-card">
                <div className="prop-section-header">
                  <h2 className="prop-section-title">
                    <Sparkles size={20} />
                    <span>Amenities & Features</span>
                  </h2>
                </div>
                <div className="prop-amenities-list">
                  {amenities.map((amenity) => (
                    <div className="prop-amenity-pill" key={amenity}>
                      <CheckCircle2 size={16} />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Video Tour Section (if applicable) */}
            <section className="prop-section-card">
              <div className="prop-section-header">
                <h2 className="prop-section-title">
                  <Video size={20} />
                  <span>Property Video Tour</span>
                </h2>
              </div>
              <div className="prop-video-wrapper">
                <iframe
                  className="prop-video-frame"
                  src="https://www.youtube.com/embed/A8EI6JaFbv4"
                  title="Property Video Tour"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </section>

            {/* Location & Interactive Map */}
            <section className="prop-section-card">
              <div className="prop-section-header">
                <h2 className="prop-section-title">
                  <Compass size={20} />
                  <span>Location Details</span>
                </h2>
              </div>

              <div className="prop-location-meta">
                <div className="prop-feature-box">
                  <span className="prop-feature-label">City</span>
                  <span className="prop-feature-val">{propertyDetails?.city_name || propertyDetails?.city?.name || "Bangalore"}</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">State</span>
                  <span className="prop-feature-val">{propertyDetails?.state_name || propertyDetails?.state?.name || "Karnataka"}</span>
                </div>
                <div className="prop-feature-box">
                  <span className="prop-feature-label">Locality / Area</span>
                  <span className="prop-feature-val">{propertyDetails?.area_name || propertyDetails?.area?.name || "Prime Zone"}</span>
                </div>
              </div>

              <div className="prop-map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3560.3838103135677!2d80.87929001488125!3d26.827742183164247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399bfe8bc34b51bb%3A0xa3ca86eec63f6f8!2sINFOSYS%20DIGITAL%20COMPUTER!5e0!3m2!1sen!2sin!4v1680238790732!5m2!1sen!2sin"
                  title="Property Location Map"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </section>
          </div>

          {/* Right Sidebar Column */}
          <aside className="prop-detail-side-col">
            {/* Direct Inquiry Form Widget */}
            <div className="prop-sidebar-card">
              <div className="prop-sidebar-header">
                <div className="prop-agent-avatar">
                  <img
                    src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop"
                    alt={propertyDetails?.user?.first_name || "Agent"}
                  />
                </div>
                <div className="prop-agent-info">
                  <h3 className="prop-agent-name">
                    {propertyDetails?.user?.first_name
                      ? `${propertyDetails?.user?.first_name} ${propertyDetails?.user?.last_name || ""}`
                      : "Verified Relationship Manager"}
                  </h3>
                  <a
                    href={`tel:${propertyDetails?.user?.mobile_number || "+919818961783"}`}
                    className="prop-agent-phone"
                  >
                    <Phone size={14} />
                    <span>{propertyDetails?.user?.mobile_number || "+91 9818961783"}</span>
                  </a>
                </div>
              </div>

              <div className="prop-sidebar-body">
                <h4 className="prop-inquiry-title">Inquire About This Property</h4>
                <p className="prop-inquiry-sub">Fill out the form below to receive exclusive brochure and schedule a private site visit.</p>

                <form onSubmit={handleInquirySubmit} className="prop-inquiry-form">
                  <div className="prop-form-group">
                    <label className="prop-form-label" htmlFor="inq-name">
                      Full Name
                    </label>
                    <input
                      id="inq-name"
                      type="text"
                      name="name"
                      className="prop-form-input"
                      placeholder="e.g. Aditi Rao"
                      required
                      value={inquiryData.name}
                      onChange={handleInquiryChange}
                    />
                  </div>

                  <div className="prop-form-group">
                    <label className="prop-form-label" htmlFor="inq-email">
                      Email Address
                    </label>
                    <input
                      id="inq-email"
                      type="email"
                      name="email_address"
                      className="prop-form-input"
                      placeholder="aditi@example.com"
                      required
                      value={inquiryData.email_address}
                      onChange={handleInquiryChange}
                    />
                  </div>

                  <div className="prop-form-group">
                    <label className="prop-form-label" htmlFor="inq-phone">
                      Contact Number
                    </label>
                    <input
                      id="inq-phone"
                      type="tel"
                      name="mobile_number"
                      className="prop-form-input"
                      placeholder="+91 98765 43210"
                      required
                      value={inquiryData.mobile_number}
                      onChange={handleInquiryChange}
                    />
                  </div>

                  <div className="prop-form-group">
                    <label className="prop-form-label" htmlFor="inq-msg">
                      Message / Notes
                    </label>
                    <textarea
                      id="inq-msg"
                      name="message"
                      className="prop-form-textarea"
                      rows={3}
                      value={inquiryData.message}
                      onChange={handleInquiryChange}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="prop-btn-submit"
                    disabled={submittingInquiry}
                  >
                    {submittingInquiry ? (
                      <span>Submitting...</span>
                    ) : (
                      <>
                        <span>Send Inquiry</span>
                        <Send size={15} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Buyer Trust & Assurance Card */}
            <div className="prop-trust-card">
              <ShieldCheck size={28} className="prop-trust-icon" />
              <div>
                <h4 className="prop-trust-title">All Home Desire Verified</h4>
                <p className="prop-trust-desc">
                  This property has been physically inspected and verified for genuine ownership, legal title clearance, and RERA approval.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
